// Code templates for different platforms

export const generateHTMLCSS = (animationName, settings) => {
  const { duration, delay, iterationCount, direction, timingFunction, fillMode } = settings;

  const keyframes = getKeyframes(animationName);

  return `<!-- HTML -->
<div class="${animationName}">
  Your Content Here
</div>

/* CSS */
.${animationName} {
  animation: ${animationName} ${duration}ms ${timingFunction} ${delay}ms ${iterationCount === 'infinite' ? 'infinite' : iterationCount} ${direction} ${fillMode};
}

@keyframes ${animationName} {
${keyframes}
}`;
};

export const generateSwiftUI = (animationName, settings) => {
  const { duration, delay, iterationCount } = settings;
  const durationSec = duration / 1000;
  const delaySec = delay / 1000;
  const repeatForever = iterationCount === 'infinite';
  const pascalName = toPascalCase(animationName);

  return `import SwiftUI

struct ${pascalName}View: View {
    @State private var isAnimating = false
    
    var body: some View {
        VStack {
            RoundedRectangle(cornerRadius: 12)
                .fill(LinearGradient(
                    gradient: Gradient(colors: [Color.purple, Color.indigo]),
                    startPoint: .topLeading,
                    endPoint: .bottomTrailing
                ))
                .frame(width: 100, height: 100)
                /* Apply ${animationName} properties */
                ${getSwiftUITransforms(animationName, 'isAnimating')}
                .animation(
                    Animation
                        .${getSwiftUITiming(settings.timingFunction)}(duration: ${durationSec})
                        .delay(${delaySec})
                        ${repeatForever ? '.repeatForever(autoreverses: true)' : ''}
                    , value: isAnimating
                )
        }
        .onAppear {
            isAnimating = true
        }
    }
}

#Preview {
    ${pascalName}View()
}`;
};

export const generateFlutter = (animationName, settings) => {
  const { duration, delay } = settings;
  const pascalName = toPascalCase(animationName);

  return `import 'package:flutter/material.dart';

class ${pascalName}Widget extends StatefulWidget {
  const ${pascalName}Widget({super.key});

  @override
  State<${pascalName}Widget> createState() => _${pascalName}WidgetState();
}

class _${pascalName}WidgetState extends State<${pascalName}Widget>
    with SingleTickerProviderStateMixin {
  late AnimationController _controller;
  late Animation<double> _animation;

  @override
  void initState() {
    super.initState();
    _controller = AnimationController(
      duration: const Duration(milliseconds: ${duration}),
      vsync: this,
    );

    _animation = CurvedAnimation(
      parent: _controller,
      curve: ${getFlutterCurve(settings.timingFunction)},
    );

    Future.delayed(const Duration(milliseconds: ${delay}), () {
      ${settings.iterationCount === 'infinite' ? '_controller.repeat(reverse: true);' : '_controller.forward();'}
    });
  }

  @override
  void dispose() {
    _controller.dispose();
    super.dispose();
  }

  @override
  Widget build(BuildContext context) {
    return AnimatedBuilder(
      animation: _animation,
      builder: (context, child) {
        return ${getFlutterWrapper(animationName, '_animation')};
      },
    );
  }
}

/* Base Widget */
Widget _buildBox() {
  return Container(
    width: 100,
    height: 100,
    decoration: BoxDecoration(
      gradient: const LinearGradient(
        colors: [Colors.purple, Colors.indigo],
        begin: Alignment.topLeft,
        end: Alignment.bottomRight,
      ),
      borderRadius: BorderRadius.circular(24),
    ),
  );
}`;
};

export const generateReactNative = (animationName, settings) => {
  const { duration, delay } = settings;
  const pascalName = toPascalCase(animationName);

  return `import React, { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet } from 'react-native';

const ${pascalName} = () => {
  const animValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const sequence = Animated.sequence([
      Animated.delay(${delay}),
      Animated.timing(animValue, {
        toValue: 1,
        duration: ${duration},
        useNativeDriver: true,
      })
    ]);

    ${settings.iterationCount === 'infinite' ? 'Animated.loop(sequence).start();' : 'sequence.start();'}
  }, []);

  const animatedStyle = ${getRNAnimatedStyle(animationName, 'animValue')};

  return (
    <View style={styles.container}>
      <Animated.View style={[styles.box, animatedStyle]} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  box: {
    width: 100,
    height: 100,
    backgroundColor: '#8B5CF6',
    borderRadius: 24,
  },
});

export default ${pascalName};`;
};

export const generateAndroid = (animationName, settings) => {
  const { duration, delay } = settings;
  const pascalName = toPascalCase(animationName);

  return `import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp

@Composable
fun ${pascalName}Animation() {
    val infiniteTransition = rememberInfiniteTransition()
    val progress by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(
                durationMillis = ${duration},
                delayMillis = ${delay},
                easing = ${getComposeEasing(settings.timingFunction)}
            ),
            repeatMode = RepeatMode.Reverse
        )
    )

    Box(
        modifier = Modifier
            .size(100.dp)
            .graphicsLayer {
                ${getComposeTransforms(animationName, 'progress')}
            }
            .background(
                brush = Brush.linearGradient(
                    colors = listOf(Color(0xFF8B5CF6), Color(0xFF6366F1))
                ),
                shape = RoundedCornerShape(24.dp)
            )
    )
}`;
};

// Helpers
function toPascalCase(str) {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

function getSwiftUITransforms(name, state) {
  const isTransformed = state;

  // Attention Seekers with specific anchors
  if (name.includes('wobble')) {
    let anchor = "center";
    if (name.includes('bottom')) anchor = "bottom";
    else if (name.includes('top')) anchor = "top";
    else if (name.includes('left')) anchor = "leading";
    else if (name.includes('right')) anchor = "trailing";

    return `.rotationEffect(Angle(degrees: ${isTransformed} ? 6 : -6), anchor: .${anchor})\n                .offset(x: ${isTransformed} ? 15 : -15)\n                .animation(.easeInOut(duration: 0.15).repeatCount(6, autoreverses: true), value: ${isTransformed})`;
  }

  if (name.includes('jello')) {
    return `.scaleEffect(x: ${isTransformed} ? 1.0 : 1.25, y: ${isTransformed} ? 1.0 : 0.75)\n                .animation(.interpolatingSpring(stiffness: 120, damping: 5).repeatCount(3), value: ${isTransformed})`;
  }

  if (name.includes('shake')) {
    let x = 0, y = 0;
    if (name.includes('horizontal')) x = 10;
    else if (name.includes('vertical')) y = 10;
    else x = 10;
    return `.offset(x: ${isTransformed} ? ${x} : -${x}, y: ${isTransformed} ? ${y} : -${y})\n                .animation(.interpolatingSpring(stiffness: 1000, damping: 10).repeatCount(5), value: ${isTransformed})`;
  }

  if (name.includes('pulsate')) {
    return `.scaleEffect(${isTransformed} ? 1.1 : 1.0)\n                .animation(.easeInOut(duration: 0.5).repeatForever(), value: ${isTransformed})`;
  }

  // Directional Slide In/Out
  if (name.includes('slide')) {
    let x = 0, y = 0;
    if (name.includes('top')) y = -100;
    else if (name.includes('bottom')) y = 100;
    else if (name.includes('left')) x = -100;
    else if (name.includes('right')) x = 100;

    if (name.includes('in')) {
      return `.offset(x: ${isTransformed} ? 0 : ${x}, y: ${isTransformed} ? 0 : ${y})\n                .opacity(${isTransformed} ? 1.0 : 0.0)`;
    } else {
      return `.offset(x: ${isTransformed} ? ${x} : 0, y: ${isTransformed} ? ${y} : 0)\n                .opacity(${isTransformed} ? 0.0 : 1.0)`;
    }
  }

  // Directional Scale
  if (name.includes('scale')) {
    let anchor = "center";
    if (name.includes('top')) anchor = "top";
    else if (name.includes('bottom')) anchor = "bottom";
    else if (name.includes('left')) anchor = "leading";
    else if (name.includes('right')) anchor = "trailing";

    const factor = name.includes('up') ? 0.5 : 1.5;
    return `.scaleEffect(${isTransformed} ? 1.0 : ${factor}, anchor: .${anchor})`;
  }

  if (name.includes('rotate')) {
    let angle = 360;
    if (name.includes('left')) angle = -360;
    return `.rotationEffect(Angle(degrees: ${isTransformed} ? ${angle} : 0))`;
  }

  if (name.includes('flip')) {
    return `.rotation3DEffect(.degrees(${isTransformed} ? 180 : 0), axis: (x: ${name.includes('horizontal') ? 1 : 0}, y: ${name.includes('vertical') ? 1 : 0}, z: 0))`;
  }

  if (name.includes('bounce')) {
    return `.offset(y: ${isTransformed} ? 0 : -50)\n                .animation(.interpolatingSpring(stiffness: 300, damping: 10), value: ${isTransformed})`;
  }

  return `.scaleEffect(${isTransformed} ? 1.0 : 0.95)`;
}

function getSwiftUITiming(timing) {
  if (timing === 'linear') return 'linear';
  if (timing.includes('ease-in-out')) return 'easeInOut';
  if (timing.includes('ease-in')) return 'easeIn';
  if (timing.includes('ease-out')) return 'easeOut';
  return 'easeInOut';
}

function getFlutterCurve(timing) {
  if (timing === 'linear') return 'Curves.linear';
  if (timing.includes('ease-in-out')) return 'Curves.easeInOut';
  if (timing.includes('ease-in')) return 'Curves.easeIn';
  if (timing.includes('ease-out')) return 'Curves.easeOut';
  return 'Curves.easeInOut';
}

function getFlutterWrapper(name, anim) {
  if (name.includes('jello')) return `Transform.scale(scale: 1.0 + (sin(${anim}.value * 20) * 0.1), child: _buildBox())`;
  if (name.includes('wobble')) {
      let alignment = "Alignment.center";
      if (name.includes('bottom')) alignment = "Alignment.bottomCenter";
      else if (name.includes('top')) alignment = "Alignment.topCenter";
      return `Transform.rotate(angle: sin(${anim}.value * 15) * 0.1, alignment: ${alignment}, child: Transform.translate(offset: Offset(sin(${anim}.value * 15) * 10, 0), child: _buildBox()))`;
  }
  if (name.includes('shake')) return `Transform.translate(offset: Offset(sin(${anim}.value * 30) * 10, 0), child: _buildBox())`;
  if (name.includes('pulsate')) return `Transform.scale(scale: 1.0 + (sin(${anim}.value * 10) * 0.1), child: _buildBox())`;
  
  if (name.includes('scale')) {
      let alignment = "Alignment.center";
      if (name.includes('top')) alignment = "Alignment.topCenter";
      else if (name.includes('bottom')) alignment = "Alignment.bottomCenter";
      const factor = name.includes('up') ? 0.5 : 1.5;
      return `Transform.scale(scale: ${factor} + (${anim}.value * (1.0 - ${factor})), alignment: ${alignment}, child: _buildBox())`;
  }
  
  if (name.includes('rotate')) {
      let alignment = "Alignment.center";
      if (name.includes('top')) alignment = "Alignment.topCenter";
      return `Transform.rotate(angle: ${anim}.value * 6.28, alignment: ${alignment}, child: _buildBox())`;
  }
  
  if (name.includes('slide')) {
    let dx = 0.0, dy = 0.0;
    if (name.includes('top')) dy = -1.0;
    else if (name.includes('bottom')) dy = 1.0;
    else if (name.includes('left')) dx = -1.0;
    else if (name.includes('right')) dx = 1.0;
    const isEnter = name.includes('in');
    return `Transform.translate(offset: Offset(${dx} * (${isEnter ? '1000 * (1.0 - ' + anim + '.value)' : '1000 * ' + anim + '.value'}), ${dy} * (${isEnter ? '1000 * (1.0 - ' + anim + '.value)' : '1000 * ' + anim + '.value'})), child: Opacity(opacity: ${isEnter ? anim + '.value' : '(1.0 - ' + anim + '.value)'}, child: _buildBox()))`;
  }

  if (name.includes('flip')) return `Transform(transform: Matrix4.identity()..rotateY(${anim}.value * 3.14), alignment: Alignment.center, child: _buildBox())`;
  if (name.includes('fade')) return `Opacity(opacity: ${name.includes('in') ? anim + '.value' : '(1.0 - ' + anim + '.value)'}, child: _buildBox())`;
  
  return `Transform.scale(scale: 0.95 + (${anim}.value * 0.05), child: _buildBox())`;
}

function getRNAnimatedStyle(name, anim) {
  if (name.includes('jello')) return `{ transform: [{ scaleX: ${anim}.interpolate({ inputRange: [0, 0.5, 1], outputRange: [1, 1.2, 1] }) }, { scaleY: ${anim}.interpolate({ inputRange: [0, 0.5, 1], outputRange: [1, 0.8, 1] }) }] }`;
  if (name.includes('shake')) return `{ transform: [{ translateX: ${anim}.interpolate({ inputRange: [0, 0.2, 0.4, 0.6, 0.8, 1], outputRange: [0, -10, 10, -10, 10, 0] }) }] }`;
  if (name.includes('scale')) return `{ transform: [{ scale: ${anim} }] }`;
  if (name.includes('rotate')) return `{ transform: [{ rotate: ${anim}.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }] }`;
  if (name.includes('fade')) return `{ opacity: ${anim} }`;
  if (name.includes('flip')) return `{ transform: [{ rotateY: ${anim}.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] }) }] }`;
  if (name.includes('slide')) return `{ transform: [{ translateY: ${anim}.interpolate({ inputRange: [0, 1], outputRange: [-100, 0] }) }] }`;
  return `{ transform: [{ scale: ${anim} }] }`;
}

function getComposeEasing(timing) {
  if (timing === 'linear') return 'LinearEasing';
  if (timing.includes('ease-in-out')) return 'FastOutSlowInEasing';
  if (timing.includes('ease-in')) return 'FastOutLinearInEasing';
  if (timing.includes('ease-out')) return 'LinearOutSlowInEasing';
  return 'FastOutSlowInEasing';
}

function getComposeTransforms(name, progress) {
  if (name.includes('jello')) return `scaleX = 1f + (kotlin.math.sin(${progress} * 20f) * 0.1f)\n                scaleY = 1f - (kotlin.math.sin(${progress} * 20f) * 0.1f)`;
  if (name.includes('wobble')) {
      return `transformOrigin = TransformOrigin(0.5f, ${name.includes('bottom') ? '1f' : name.includes('top') ? '0f' : '0.5f'})\n                rotationZ = kotlin.math.sin(${progress} * 15f) * 5f\n                translationX = kotlin.math.sin(${progress} * 15f) * 10f`;
  }
  if (name.includes('shake')) return `translationX = kotlin.math.sin(${progress} * 30f) * 10f`;
  
  if (name.includes('scale')) {
      const startFactor = name.includes('up') ? 0.5 : 1.5;
      return `transformOrigin = TransformOrigin(0.5f, ${name.includes('bottom') ? '1f' : name.includes('top') ? '0f' : '0.5f'})\n                scaleX = ${startFactor}f + (${progress} * (1f - ${startFactor}f))\n                scaleY = ${startFactor}f + (${progress} * (1f - ${startFactor}f))`;
  }
  
  if (name.includes('rotate')) return `rotationZ = ${progress} * 360f`;
  if (name.includes('fade')) return `alpha = ${name.includes('in') ? progress : '1f - ' + progress}`;
  if (name.includes('flip')) return `rotationY = ${progress} * 180f`;
  
  if (name.includes('slide')) {
      const start = name.includes('in') ? 1000 : 0;
      const end = name.includes('in') ? 0 : 1000;
      let x = 0, y = 0;
      if (name.includes('top')) y = -1;
      else if (name.includes('bottom')) y = 1;
      else if (name.includes('left')) x = -1;
      else if (name.includes('right')) x = 1;
      return `translationX = ${x}f * (${start}f + (${progress} * (${end}f - ${start}f)))\n                translationY = ${y}f * (${start}f + (${progress} * (${end}f - ${start}f)))\n                alpha = ${name.includes('in') ? progress : '1f - ' + progress}`;
  }

  return `scaleX = 0.9f + (${progress} * 0.1f)\n                scaleY = 0.9f + (${progress} * 0.1f)`;
}

function getKeyframes(animationName) {
  const keyframesMap = {
    "pulsate-fwd": "  0% {\n    transform: scale(1);\n  }\n  50% {\n    transform: scale(1.1);\n  }\n  100% {\n    transform: scale(1);\n  }",
    "pulsate-bck": "  0% {\n    transform: scale(1);\n  }\n  50% {\n    transform: scale(0.9);\n  }\n  100% {\n    transform: scale(1);\n  }",
    "pulsate-fwd-bck": "  0%, 100% {\n    transform: scale(1);\n  }\n  25% {\n    transform: scale(1.1);\n  }\n  75% {\n    transform: scale(0.9);\n  }",
    "shake-horizontal": "  0%, 100% {\n    transform: translateX(0);\n  }\n  10%, 30%, 50%, 70%, 90% {\n    transform: translateX(-10px);\n  }\n  20%, 40%, 60%, 80% {\n    transform: translateX(10px);\n  }",
    "shake-vertical": "  0%, 100% {\n    transform: translateY(0);\n  }\n  10%, 30%, 50%, 70%, 90% {\n    transform: translateY(-8px);\n  }\n  20%, 40%, 60%, 80% {\n    transform: translateY(8px);\n  }",
    "shake-lr": "  0%, 100% {\n    transform: rotate(0deg);\n    transform-origin: 50% 50%;\n  }\n  10% {\n    transform: rotate(8deg);\n  }\n  20%, 40%, 60% {\n    transform: rotate(-10deg);\n  }\n  30%, 50%, 70% {\n    transform: rotate(10deg);\n  }\n  80% {\n    transform: rotate(-8deg);\n  }\n  90% {\n    transform: rotate(8deg);\n  }",
    "wobble-hor-bottom": "  0%, 100% {\n    transform: translateX(0%);\n    transform-origin: 50% 50%;\n  }\n  15% {\n    transform: translateX(-30px) rotate(-6deg);\n  }\n  30% {\n    transform: translateX(15px) rotate(6deg);\n  }\n  45% {\n    transform: translateX(-15px) rotate(-3.6deg);\n  }\n  60% {\n    transform: translateX(9px) rotate(2.4deg);\n  }\n  75% {\n    transform: translateX(-6px) rotate(-1.2deg);\n  }",
    "wobble-hor-top": "  0%, 100% {\n    transform: translateX(0%);\n    transform-origin: 50% 50%;\n  }\n  15% {\n    transform: translateX(-30px) rotate(6deg);\n  }\n  30% {\n    transform: translateX(15px) rotate(-6deg);\n  }\n  45% {\n    transform: translateX(-15px) rotate(3.6deg);\n  }\n  60% {\n    transform: translateX(9px) rotate(-2.4deg);\n  }\n  75% {\n    transform: translateX(-6px) rotate(1.2deg);\n  }",
    "wobble-ver-left": "  0%, 100% {\n    transform: translateY(0) rotate(0);\n    transform-origin: 50% 50%;\n  }\n  15% {\n    transform: translateY(-30px) rotate(-6deg);\n  }\n  30% {\n    transform: translateY(15px) rotate(6deg);\n  }\n  45% {\n    transform: translateY(-15px) rotate(-3.6deg);\n  }\n  60% {\n    transform: translateY(9px) rotate(2.4deg);\n  }\n  75% {\n    transform: translateY(-6px) rotate(-1.2deg);\n  }",
    "wobble-ver-right": "  0%, 100% {\n    transform: translateY(0) rotate(0);\n    transform-origin: 50% 50%;\n  }\n  15% {\n    transform: translateY(-30px) rotate(6deg);\n  }\n  30% {\n    transform: translateY(15px) rotate(-6deg);\n  }\n  45% {\n    transform: translateY(-15px) rotate(3.6deg);\n  }\n  60% {\n    transform: translateY(9px) rotate(-2.4deg);\n  }\n  75% {\n    transform: translateY(-6px) rotate(1.2deg);\n  }",
    "bounce-top": "  0% {\n    transform: translateY(-45px);\n    animation-timing-function: ease-in;\n    opacity: 1;\n  }\n  24% {\n    opacity: 1;\n  }\n  40% {\n    transform: translateY(-24px);\n    animation-timing-function: ease-in;\n  }\n  65% {\n    transform: translateY(-12px);\n    animation-timing-function: ease-in;\n  }\n  82% {\n    transform: translateY(-6px);\n    animation-timing-function: ease-in;\n  }\n  93% {\n    transform: translateY(-4px);\n    animation-timing-function: ease-in;\n  }\n  25%, 55%, 75%, 87%, 98%, 100% {\n    transform: translateY(0px);\n    animation-timing-function: ease-out;\n  }",
    "bounce-bottom": "  0% {\n    transform: translateY(45px);\n    animation-timing-function: ease-in;\n    opacity: 1;\n  }\n  24% {\n    opacity: 1;\n  }\n  40% {\n    transform: translateY(24px);\n    animation-timing-function: ease-in;\n  }\n  65% {\n    transform: translateY(12px);\n    animation-timing-function: ease-in;\n  }\n  82% {\n    transform: translateY(6px);\n    animation-timing-function: ease-in;\n  }\n  93% {\n    transform: translateY(4px);\n    animation-timing-function: ease-in;\n  }\n  25%, 55%, 75%, 87%, 98%, 100% {\n    transform: translateY(0px);\n    animation-timing-function: ease-out;\n  }",
    "bounce-left": "  0% {\n    transform: translateX(-45px);\n    animation-timing-function: ease-in;\n    opacity: 1;\n  }\n  24% {\n    opacity: 1;\n  }\n  40% {\n    transform: translateX(-24px);\n    animation-timing-function: ease-in;\n  }\n  65% {\n    transform: translateX(-12px);\n    animation-timing-function: ease-in;\n  }\n  82% {\n    transform: translateX(-6px);\n    animation-timing-function: ease-in;\n  }\n  93% {\n    transform: translateX(-4px);\n    animation-timing-function: ease-in;\n  }\n  25%, 55%, 75%, 87%, 98%, 100% {\n    transform: translateX(0px);\n    animation-timing-function: ease-out;\n  }",
    "bounce-right": "  0% {\n    transform: translateX(45px);\n    animation-timing-function: ease-in;\n    opacity: 1;\n  }\n  24% {\n    opacity: 1;\n  }\n  40% {\n    transform: translateX(24px);\n    animation-timing-function: ease-in;\n  }\n  65% {\n    transform: translateX(12px);\n    animation-timing-function: ease-in;\n  }\n  82% {\n    transform: translateX(6px);\n    animation-timing-function: ease-in;\n  }\n  93% {\n    transform: translateX(4px);\n    animation-timing-function: ease-in;\n  }\n  25%, 55%, 75%, 87%, 98%, 100% {\n    transform: translateX(0px);\n    animation-timing-function: ease-out;\n  }",
    "jello-horizontal": "  0% {\n    transform: scale3d(1, 1, 1);\n  }\n  30% {\n    transform: scale3d(1.25, 0.75, 1);\n  }\n  40% {\n    transform: scale3d(0.75, 1.25, 1);\n  }\n  50% {\n    transform: scale3d(1.15, 0.85, 1);\n  }\n  65% {\n    transform: scale3d(0.95, 1.05, 1);\n  }\n  75% {\n    transform: scale3d(1.05, 0.95, 1);\n  }\n  100% {\n    transform: scale3d(1, 1, 1);\n  }",
    "jello-vertical": "  0% {\n    transform: scale3d(1, 1, 1);\n  }\n  30% {\n    transform: scale3d(0.75, 1.25, 1);\n  }\n  40% {\n    transform: scale3d(1.25, 0.75, 1);\n  }\n  50% {\n    transform: scale3d(0.85, 1.15, 1);\n  }\n  65% {\n    transform: scale3d(1.05, 0.95, 1);\n  }\n  75% {\n    transform: scale3d(0.95, 1.05, 1);\n  }\n  100% {\n    transform: scale3d(1, 1, 1);\n  }",
    "jello-diagonal": "  0% {\n    transform: skewX(0deg) skewY(0deg);\n  }\n  30% {\n    transform: skewX(10deg) skewY(10deg);\n  }\n  40% {\n    transform: skewX(-8deg) skewY(-8deg);\n  }\n  50% {\n    transform: skewX(6deg) skewY(6deg);\n  }\n  60% {\n    transform: skewX(-4deg) skewY(-4deg);\n  }\n  70% {\n    transform: skewX(2deg) skewY(2deg);\n  }\n  80% {\n    transform: skewX(-1deg) skewY(-1deg);\n  }\n  100% {\n    transform: skewX(0deg) skewY(0deg);\n  }",
    "vibrate-1": "  0%, 100% {\n    transform: translate(0);\n  }\n  20% {\n    transform: translate(-2px, 2px);\n  }\n  40% {\n    transform: translate(-2px, -2px);\n  }\n  60% {\n    transform: translate(2px, 2px);\n  }\n  80% {\n    transform: translate(2px, -2px);\n  }",
    "vibrate-2": "  0%, 100% {\n    transform: translate(0);\n  }\n  20% {\n    transform: translate(2px, -2px);\n  }\n  40% {\n    transform: translate(2px, 2px);\n  }\n  60% {\n    transform: translate(-2px, 2px);\n  }\n  80% {\n    transform: translate(-2px, -2px);\n  }",
    "vibrate-3": "  0%, 100% {\n    transform: translate(0);\n  }\n  10% {\n    transform: translate(-1px, -1px);\n  }\n  20% {\n    transform: translate(1px, -1px);\n  }\n  30% {\n    transform: translate(-1px, 1px);\n  }\n  40% {\n    transform: translate(1px, 1px);\n  }\n  50% {\n    transform: translate(-1px, -1px);\n  }\n  60% {\n    transform: translate(1px, -1px);\n  }\n  70% {\n    transform: translate(-1px, 1px);\n  }\n  80% {\n    transform: translate(-1px, -1px);\n  }\n  90% {\n    transform: translate(1px, 1px);\n  }",
    "flicker-1": "  0%, 100% {\n    opacity: 1;\n  }\n  41.99% {\n    opacity: 1;\n  }\n  42% {\n    opacity: 0;\n  }\n  43% {\n    opacity: 0;\n  }\n  43.01% {\n    opacity: 1;\n  }\n  47.99% {\n    opacity: 1;\n  }\n  48% {\n    opacity: 0;\n  }\n  49% {\n    opacity: 0;\n  }\n  49.01% {\n    opacity: 1;\n  }",
    "flicker-2": "  0%, 100% {\n    opacity: 1;\n  }\n  31.99% {\n    opacity: 1;\n  }\n  32% {\n    opacity: 0;\n  }\n  32.99% {\n    opacity: 0;\n  }\n  33% {\n    opacity: 1;\n  }\n  63% {\n    opacity: 1;\n  }\n  63.01% {\n    opacity: 0;\n  }\n  64% {\n    opacity: 0;\n  }\n  64.01% {\n    opacity: 1;\n  }\n  67% {\n    opacity: 1;\n  }\n  67.01% {\n    opacity: 0;\n  }\n  69% {\n    opacity: 0;\n  }\n  69.01% {\n    opacity: 1;\n  }",
    "flicker-3": "  0%, 100% {\n    opacity: 1;\n  }\n  32.98% {\n    opacity: 1;\n  }\n  33% {\n    opacity: 0;\n  }\n  34% {\n    opacity: 0;\n  }\n  34.02% {\n    opacity: 1;\n  }\n  34.98% {\n    opacity: 1;\n  }\n  35% {\n    opacity: 0;\n  }\n  35.98% {\n    opacity: 0;\n  }\n  36% {\n    opacity: 1;\n  }\n  36.98% {\n    opacity: 1;\n  }\n  37% {\n    opacity: 0;\n  }\n  37.98% {\n    opacity: 0;\n  }\n  38% {\n    opacity: 1;\n  }\n  67.98% {\n    opacity: 1;\n  }\n  68% {\n    opacity: 0;\n  }\n  68.98% {\n    opacity: 0;\n  }\n  69% {\n    opacity: 1;\n  }",
    "blink-1": "  0%, 50%, 100% {\n    opacity: 1;\n  }\n  25%, 75% {\n    opacity: 0;\n  }",
    "blink-2": "  0% {\n    opacity: 1;\n  }\n  50% {\n    opacity: 0;\n  }\n  100% {\n    opacity: 1;\n  }",
    "scale-up-center": "  0% {\n    transform: scale(0.5);\n  }\n  100% {\n    transform: scale(1);\n  }",
    "scale-up-top": "  0% {\n    transform: scale(0.5);\n    transform-origin: 50% 0%;\n  }\n  100% {\n    transform: scale(1);\n    transform-origin: 50% 0%;\n  }",
    "scale-up-bottom": "  0% {\n    transform: scale(0.5);\n    transform-origin: 50% 100%;\n  }\n  100% {\n    transform: scale(1);\n    transform-origin: 50% 100%;\n  }",
    "scale-up-left": "  0% {\n    transform: scale(0.5);\n    transform-origin: 0% 50%;\n  }\n  100% {\n    transform: scale(1);\n    transform-origin: 0% 50%;\n  }",
    "scale-up-right": "  0% {\n    transform: scale(0.5);\n    transform-origin: 100% 50%;\n  }\n  100% {\n    transform: scale(1);\n    transform-origin: 100% 50%;\n  }",
    "scale-down-center": "  0% {\n    transform: scale(1);\n  }\n  100% {\n    transform: scale(0.5);\n  }",
    "scale-down-top": "  0% {\n    transform: scale(1);\n    transform-origin: 50% 0%;\n  }\n  100% {\n    transform: scale(0.5);\n    transform-origin: 50% 0%;\n  }",
    "scale-down-bottom": "  0% {\n    transform: scale(1);\n    transform-origin: 50% 100%;\n  }\n  100% {\n    transform: scale(0.5);\n    transform-origin: 50% 100%;\n  }",
    "rotate-center": "  0% {\n    transform: rotate(0);\n  }\n  100% {\n    transform: rotate(360deg);\n  }",
    "rotate-top": "  0% {\n    transform: rotate(0);\n    transform-origin: top;\n  }\n  100% {\n    transform: rotate(360deg);\n    transform-origin: top;\n  }",
    "rotate-bottom": "  0% {\n    transform: rotate(0);\n    transform-origin: bottom;\n  }\n  100% {\n    transform: rotate(360deg);\n    transform-origin: bottom;\n  }",
    "flip-horizontal": "  0% {\n    transform: rotateX(0);\n  }\n  100% {\n    transform: rotateX(-180deg);\n  }",
    "flip-vertical": "  0% {\n    transform: rotateY(0);\n  }\n  100% {\n    transform: rotateY(-180deg);\n  }",
    "flip-diagonal": "  0% {\n    transform: rotate3d(1, 1, 0, 0deg);\n  }\n  100% {\n    transform: rotate3d(1, 1, 0, -180deg);\n  }",
    "slide-top": "  0% {\n    transform: translateY(0);\n  }\n  100% {\n    transform: translateY(-100px);\n  }",
    "slide-bottom": "  0% {\n    transform: translateY(0);\n  }\n  100% {\n    transform: translateY(100px);\n  }",
    "slide-left": "  0% {\n    transform: translateX(0);\n  }\n  100% {\n    transform: translateX(-100px);\n  }",
    "slide-right": "  0% {\n    transform: translateX(0);\n  }\n  100% {\n    transform: translateX(100px);\n  }",
    "fade-in": "  0% {\n    opacity: 0;\n  }\n  100% {\n    opacity: 1;\n  }",
    "fade-in-top": "  0% {\n    transform: translateY(-50px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateY(0);\n    opacity: 1;\n  }",
    "fade-in-bottom": "  0% {\n    transform: translateY(50px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateY(0);\n    opacity: 1;\n  }",
    "fade-in-left": "  0% {\n    transform: translateX(-50px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0);\n    opacity: 1;\n  }",
    "fade-in-right": "  0% {\n    transform: translateX(50px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0);\n    opacity: 1;\n  }",
    "slide-in-top": "  0% {\n    transform: translateY(-1000px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateY(0);\n    opacity: 1;\n  }",
    "slide-in-bottom": "  0% {\n    transform: translateY(1000px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateY(0);\n    opacity: 1;\n  }",
    "slide-in-left": "  0% {\n    transform: translateX(-1000px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0);\n    opacity: 1;\n  }",
    "slide-in-right": "  0% {\n    transform: translateX(1000px);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0);\n    opacity: 1;\n  }",
    "bounce-in": "  0% {\n    transform: scale(0);\n    animation-timing-function: ease-in;\n    opacity: 0;\n  }\n  38% {\n    transform: scale(1.1);\n    animation-timing-function: ease-out;\n    opacity: 1;\n  }\n  55% {\n    transform: scale(0.9);\n    animation-timing-function: ease-in;\n  }\n  72% {\n    transform: scale(1.04);\n    animation-timing-function: ease-out;\n  }\n  81% {\n    transform: scale(0.97);\n    animation-timing-function: ease-in;\n  }\n  89% {\n    transform: scale(1.01);\n    animation-timing-function: ease-out;\n  }\n  95% {\n    transform: scale(0.99);\n    animation-timing-function: ease-in;\n  }\n  100% {\n    transform: scale(1);\n    animation-timing-function: ease-out;\n    opacity: 1;\n  }",
    "bounce-in-top": "  0% {\n    transform: translateY(-500px);\n    animation-timing-function: ease-in;\n    opacity: 0;\n  }\n  38% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n    opacity: 1;\n  }\n  55% {\n    transform: translateY(-65px);\n    animation-timing-function: ease-in;\n  }\n  72% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }\n  81% {\n    transform: translateY(-28px);\n    animation-timing-function: ease-in;\n  }\n  90% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }\n  95% {\n    transform: translateY(-8px);\n    animation-timing-function: ease-in;\n  }\n  100% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }",
    "bounce-in-bottom": "  0% {\n    transform: translateY(500px);\n    animation-timing-function: ease-in;\n    opacity: 0;\n  }\n  38% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n    opacity: 1;\n  }\n  55% {\n    transform: translateY(65px);\n    animation-timing-function: ease-in;\n  }\n  72% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }\n  81% {\n    transform: translateY(28px);\n    animation-timing-function: ease-in;\n  }\n  90% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }\n  95% {\n    transform: translateY(8px);\n    animation-timing-function: ease-in;\n  }\n  100% {\n    transform: translateY(0);\n    animation-timing-function: ease-out;\n  }",
    "roll-in-left": "  0% {\n    transform: translateX(-800px) rotate(-540deg);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0) rotate(0deg);\n    opacity: 1;\n  }",
    "roll-in-right": "  0% {\n    transform: translateX(800px) rotate(540deg);\n    opacity: 0;\n  }\n  100% {\n    transform: translateX(0) rotate(0deg);\n    opacity: 1;\n  }",
    "fade-out": "  0% {\n    opacity: 1;\n  }\n  100% {\n    opacity: 0;\n  }",
    "fade-out-top": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(-50px);\n    opacity: 0;\n  }",
    "fade-out-bottom": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(50px);\n    opacity: 0;\n  }",
    "slide-out-top": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(-1000px);\n    opacity: 0;\n  }",
    "slide-out-bottom": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(1000px);\n    opacity: 0;\n  }",
    "slide-out-left": "  0% {\n    transform: translateX(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateX(-1000px);\n    opacity: 0;\n  }",
    "slide-out-right": "  0% {\n    transform: translateX(0);\n    opacity: 1;\n  }\n  100% {\n    transform: translateX(1000px);\n    opacity: 0;\n  }",
    "bounce-out": "  0% {\n    transform: scale(1);\n    opacity: 1;\n  }\n  20% {\n    transform: scale(0.9);\n  }\n  50%, 55% {\n    transform: scale(1.1);\n    opacity: 1;\n  }\n  100% {\n    transform: scale(0);\n    opacity: 0;\n  }",
    "bounce-out-top": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  20% {\n    transform: translateY(20px);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(-1000px);\n    opacity: 0;\n  }",
    "bounce-out-bottom": "  0% {\n    transform: translateY(0);\n    opacity: 1;\n  }\n  20% {\n    transform: translateY(-20px);\n    opacity: 1;\n  }\n  100% {\n    transform: translateY(1000px);\n    opacity: 0;\n  }",
    "tracking-in-expand": "  0% {\n    letter-spacing: -0.5em;\n    opacity: 0;\n  }\n  40% {\n    opacity: 0.6;\n  }\n  100% {\n    letter-spacing: normal;\n    opacity: 1;\n  }",
    "tracking-in-contract": "  0% {\n    letter-spacing: 1em;\n    opacity: 0;\n  }\n  40% {\n    opacity: 0.6;\n  }\n  100% {\n    letter-spacing: normal;\n    opacity: 1;\n  }",
    "tracking-out-expand": "  0% {\n    letter-spacing: normal;\n    opacity: 1;\n  }\n  60% {\n    opacity: 0.6;\n  }\n  100% {\n    letter-spacing: 1em;\n    opacity: 0;\n  }",
    "focus-in-expand": "  0% {\n    letter-spacing: -0.5em;\n    filter: blur(12px);\n    opacity: 0;\n  }\n  100% {\n    letter-spacing: normal;\n    filter: blur(0);\n    opacity: 1;\n  }",
    "focus-in-contract": "  0% {\n    letter-spacing: 1em;\n    filter: blur(12px);\n    opacity: 0;\n  }\n  100% {\n    letter-spacing: normal;\n    filter: blur(0);\n    opacity: 1;\n  }",
    "blur-out-expand": "  0% {\n    filter: blur(0);\n    opacity: 1;\n  }\n  100% {\n    letter-spacing: 1em;\n    filter: blur(12px);\n    opacity: 0;\n  }",
    "blur-out-contract": "  0% {\n    letter-spacing: normal;\n    filter: blur(0);\n    opacity: 1;\n  }\n  100% {\n    letter-spacing: -0.5em;\n    filter: blur(12px);\n    opacity: 0;\n  }",
    "color-change-2x": "  0% {\n    background: #19dcea;\n  }\n  100% {\n    background: #b22cff;\n  }",
    "color-change-3x": "  0% {\n    background: #19dcea;\n  }\n  50% {\n    background: #b22cff;\n  }\n  100% {\n    background: #ea2222;\n  }",
    "color-change-4x": "  0% {\n    background: #19dcea;\n  }\n  33% {\n    background: #b22cff;\n  }\n  66% {\n    background: #ea2222;\n  }\n  100% {\n    background: #f5be10;\n  }",
    "bg-pan-left": "  0% {\n    background-position: 100% 50%;\n  }\n  100% {\n    background-position: 0% 50%;\n  }",
    "bg-pan-right": "  0% {\n    background-position: 0% 50%;\n  }\n  100% {\n    background-position: 100% 50%;\n  }",
    "bg-pan-top": "  0% {\n    background-position: 50% 100%;\n  }\n  100% {\n    background-position: 50% 0%;\n  }",
    "bg-pan-bottom": "  0% {\n    background-position: 50% 0%;\n  }\n  100% {\n    background-position: 50% 100%;\n  }"
  };

  return keyframesMap[animationName] || `  0% { transform: scale(0.5); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }`;
}
